import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Text, useBreakpoint } from '@factorialco/deprecated-design-system'
import { F0Button, F0Card } from '@factorialco/f0-react'
import { AutoGrid, Carousel, Spinner } from '@factorialco/f0-react/dist/experimental'
import { AlertCircleLine, CheckCircleLine, Pig } from '@factorialco/f0-react/icons/app'
import {
  BillingAddonReferenceFragment,
  GetUpsellDiscountsDocument,
  MarketplaceModulesDataCollectionDocument,
  MarketplaceModulesDataCollectionQuery,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { locale } from 'modules/marketplace_modules/components/utils/locales'
import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'
import { useModuleStatus } from 'modules/marketplace_modules/hooks/useModuleStatus'
import { getInstalledStatus } from 'modules/marketplace_modules/lib/getInstalledStatus'
import { useLocalStorageWithExpiration } from 'modules/upselling_kit/utils/localStorage'

type ModuleFromQuery = NonNullable<
  MarketplaceModulesDataCollectionQuery['modules']
>['datoModulesConnection']['nodes'][number]

type IncludedInPlanFromQuery = NonNullable<ModuleFromQuery['includedInPlan']>[number]

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
const CATEGORY_ORDER = ['Finance Hub', 'Talent Hub', 'IT Management', 'Operations Hub']

const findFirstValidImage = (module: ModuleFromQuery): string => {
  const firstImage = module.carousel?.find((item) => {
    const url = item?.url?.toLowerCase() || ''

    return IMAGE_EXTENSIONS.some((ext) => url.includes(ext))
  })

  return firstImage?.url || module.thumbnailImage?.url || ''
}

const getModuleAddonReferences = (
  modules: ModuleFromQuery[]
): BillingAddonReferenceFragment[][] => {
  return modules
    .filter((module) => module.billingAddonReference && module.billingAddonReference.length > 0)
    .map((module) => module.billingAddonReference || [])
    .filter((refs) => refs.length > 0)
}

const getModuleIncludedInPlan = (modules: ModuleFromQuery[]) => {
  return modules
    .filter((module) => module.includedInPlan && module.includedInPlan.length > 0)
    .map((module) => ({
      moduleName: module.name || '',
      includedInPlan: module.includedInPlan || [],
    }))
}

/*
 * Displays marketplace modules grouped by category with their installation status.
 */
const List = () => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  )

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isLargeScreen = windowWidth >= 1600

  const navigate = useNavigate()
  const { access, company } = useCurrentSession()
  const location = useLocation()
  const { search: searchParams } = location
  const { getDetailPath } = useMarketplaceModulesPath()

  const queryParams = useMemo(() => new URLSearchParams(searchParams), [searchParams])
  const upsellOrigin = queryParams.get('upsell_origin')
  const preview = queryParams.get('preview') === 'true'

  const [storedUpsellOrigin, setUpsellOrigin] = useLocalStorageWithExpiration<{
    request_source: string
    request_source_category?: string
    request_source_component?: string
  }>('upsell_origin', 5)
  const isLocalStorageExpired = storedUpsellOrigin === null

  const datoFormatLocale = locale[access.locale]
  const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')

  const { data: discountsData } = useQuery(GetUpsellDiscountsDocument)
  const [moduleAddonReferences, setModuleAddonReferences] = useState<
    BillingAddonReferenceFragment[][]
  >([])
  const [moduleIncludedInPlan, setModuleIncludedInPlan] = useState<
    Array<{ moduleName: string; includedInPlan: IncludedInPlanFromQuery[] }>
  >([])

  const { data: modulesData, loading: modulesLoading } = useQuery(
    MarketplaceModulesDataCollectionDocument,
    {
      variables: {
        countryCode: countryCodeForCompany,
        locale: datoFormatLocale,
        preview,
      },
    }
  )

  const { getModuleStatus: getModuleStatusFromHook, loading: moduleStatusLoading } =
    useModuleStatus(moduleAddonReferences, {})

  useEffect(() => {
    if (modulesData?.modules.datoModulesConnection.nodes) {
      const nodes = modulesData.modules.datoModulesConnection.nodes
      setModuleAddonReferences(getModuleAddonReferences(nodes))
      setModuleIncludedInPlan(getModuleIncludedInPlan(nodes))
    }
  }, [modulesData])

  const getModuleStatus = useCallback(
    (module: ModuleFromQuery) => {
      return getModuleStatusFromHook({
        moduleName: module.name || null,
        billingAddonReference: module.billingAddonReference || null,
        moduleIncludedInPlan,
      })
    },
    [getModuleStatusFromHook, moduleIncludedInPlan]
  )

  const handleModuleClick = useCallback(
    (module: ModuleFromQuery) => {
      track(trackingEvents.marketplaceModules.clickModuleCard, {
        component: 'marketplace_modules_list',
        module_name: module.name || '',
      })

      if (isLocalStorageExpired && !upsellOrigin) {
        setUpsellOrigin({
          request_source: 'Marketplace',
          request_source_category: 'Organic',
        })
      }

      const upsellOriginParam = upsellOrigin || (isLocalStorageExpired ? 'Marketplace' : undefined)

      navigate(
        getDetailPath(module.slug, upsellOriginParam ? `upsell_origin=${upsellOriginParam}` : ''),
        { state: { from: location.pathname } }
      )
    },
    [
      isLocalStorageExpired,
      upsellOrigin,
      setUpsellOrigin,
      navigate,
      getDetailPath,
      location.pathname,
    ]
  )

  const getModuleDiscount = useCallback(
    (module: ModuleFromQuery) => {
      return discountsData?.upsell?.upsellDiscountsConnection?.nodes?.find(
        (discountItem) => discountItem.module === module.chargebeeModuleName
      )
    },
    [discountsData]
  )

  const getModuleMetadata = useCallback(
    (module: ModuleFromQuery) => {
      const status = getModuleStatus(module)
      const discount = getModuleDiscount(module)
      const label = getInstalledStatus(
        status.isInstalled,
        status.variant,
        status.hasEnterpriseVariant ?? false,
        status.from
      )

      type MetadataItem = {
        icon: typeof CheckCircleLine
        property: {
          type: 'status'
          value: {
            label: string
            status: 'positive' | 'neutral' | 'warning'
          }
        }
      }

      const metadata: MetadataItem[] = [
        {
          icon: status.isInstalled ? CheckCircleLine : AlertCircleLine,
          property: {
            type: 'status' as const,
            value: {
              label: label.label,
              status: status.isInstalled ? 'positive' : 'neutral',
            },
          },
        },
      ]

      if (discount && !status.isInstalled) {
        metadata.push({
          icon: Pig,
          property: {
            type: 'status' as const,
            value: {
              label: i18n.t('marketplace.revamp.list.discount', {
                percentage: Math.floor(discount?.percentage || 0),
              }),
              status: 'warning',
            },
          },
        })
      }

      return metadata
    },
    [getModuleStatus, getModuleDiscount]
  )

  const modules = useMemo(() => {
    return modulesData?.modules.datoModulesConnection.nodes || []
  }, [modulesData])

  const notInstalledModules = useMemo(() => {
    return modules.filter((module: ModuleFromQuery) => !getModuleStatus(module).isInstalled)
  }, [modules, getModuleStatus])

  const modulesByCategory = useMemo(() => {
    const grouped = new Map<
      string,
      { category: ModuleFromQuery['category']; modules: ModuleFromQuery[] }
    >()

    for (const module of modules) {
      const categoryId = module.category?.id || 'uncategorized'
      const existing = grouped.get(categoryId)

      if (existing) {
        existing.modules.push(module)
      } else {
        grouped.set(categoryId, { category: module.category, modules: [module] })
      }
    }

    return Array.from(grouped.values())
      .sort((a, b) => {
        const aIndex = CATEGORY_ORDER.indexOf(a.category?.name || '')
        const bIndex = CATEGORY_ORDER.indexOf(b.category?.name || '')

        return (
          (aIndex === -1 ? CATEGORY_ORDER.length : aIndex) -
          (bIndex === -1 ? CATEGORY_ORDER.length : bIndex)
        )
      })
      .map((group) => ({
        ...group,
        modules: group.modules.sort((a, b) => {
          const aInstalled = getModuleStatus(a).isInstalled
          const bInstalled = getModuleStatus(b).isInstalled

          if (!aInstalled && bInstalled) return -1
          if (aInstalled && !bInstalled) return 1

          return 0
        }),
      }))
  }, [modules, getModuleStatus])

  const containerStyle = isLargeScreen
    ? { maxWidth: '1600px', width: '100%', marginLeft: 'auto', marginRight: 'auto' }
    : undefined
  const isLoading = modulesLoading || moduleStatusLoading

  const renderModuleCard = (module: ModuleFromQuery) => (
    <F0Card
      key={module.id}
      avatar={{
        type: 'company',
        name: module.translatedName || '',
        src: module.image?.url || '',
      }}
      onClick={() => handleModuleClick(module)}
      image={findFirstValidImage(module)}
      title={module.translatedName || ''}
      description={module.shortDescription || ''}
      metadata={getModuleMetadata(module)}
    />
  )

  const renderCategoryHeader = (category: ModuleFromQuery['category']) => {
    switch (category?.name) {
      case 'Finance Hub':
        return (
          <Box key={category?.id}>
            <Text.Normal size="400" weight="bold" color="black">
              {i18n.t('marketplace.revamp.list.sections.finance.title')}
            </Text.Normal>
            <Text.Normal size="200" color="black">
              {i18n.t('marketplace.revamp.list.sections.finance.description')}
            </Text.Normal>
          </Box>
        )
      case 'Talent Hub':
        return (
          <Box key={category?.id}>
            <Text.Normal size="400" weight="bold" color="black">
              {i18n.t('marketplace.revamp.list.sections.talent.title')}
            </Text.Normal>
            <Text.Normal size="200" color="black">
              {i18n.t('marketplace.revamp.list.sections.talent.description')}
            </Text.Normal>
          </Box>
        )
      case 'Operations Hub':
        return (
          <Box key={category?.id}>
            <Text.Normal size="400" weight="bold" color="black">
              {i18n.t('marketplace.revamp.list.sections.operations.title')}
            </Text.Normal>
            <Text.Normal size="200" color="black">
              {i18n.t('marketplace.revamp.list.sections.operations.description')}
            </Text.Normal>
          </Box>
        )
      case 'IT Management':
        return (
          <Box key={category?.id}>
            <Text.Normal size="400" weight="bold" color="black">
              {i18n.t('marketplace.revamp.list.sections.it.title')}
            </Text.Normal>
            <Text.Normal size="200" color="black">
              {i18n.t('marketplace.revamp.list.sections.it.description')}
            </Text.Normal>
          </Box>
        )
      default:
        return null
    }
  }

  return (
    <Box style={containerStyle} gap="s24">
      <Box
        background="grey200"
        padding="s16"
        flexDirection="row"
        gap="s24"
        style={{ width: '100%' }}
        alignItems="center"
        justifyContent="center"
      >
        <Box
          flexDirection="row"
          justifyContent="spaceBetween"
          style={{ maxWidth: '1600px', width: '100%' }}
        >
          <Box flexDirection="column" gap="s16" padding="s48" style={{ maxWidth: '620px' }}>
            <span
              style={{ fontWeight: 'bold', color: 'black', fontSize: '32px', lineHeight: '32px' }}
            >
              {i18n.t('marketplace.revamp.list.carousel.title')}
            </span>
            <span
              style={{
                color: '#0116379C',
                fontSize: '20px',
                maxWidth: '600px',
                lineHeight: '24px',
                letterSpacing: '-0.2px',
              }}
            >
              {i18n.t('marketplace.revamp.list.carousel.description')}
            </span>
            <Box width="fit-content">
              <F0Button
                label={i18n.t('marketplace.revamp.list.carousel.button_label')}
                onClick={() => {
                  const element = document.getElementById('marketplace-modules-list')
                  if (element) {
                    element.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                      inline: 'nearest',
                    })
                  }

                  track(trackingEvents.marketplaceModules.clickExploreModulesButton, {
                    component: 'marketplace_header',
                  })
                }}
              />
            </Box>
          </Box>
          <Box gap="s24" style={{ maxWidth: isLargeScreen ? '800px' : '450px', width: '100%' }}>
            <Carousel
              showArrows
              showPeek
              columns={{
                default: 1,
                xl: 2,
                lg: 2,
                md: 2,
                sm: 1,
                xs: 1,
              }}
            >
              {notInstalledModules.map((module: ModuleFromQuery) => (
                <Box key={module.id}>{renderModuleCard(module)}</Box>
              ))}
            </Carousel>
          </Box>
        </Box>
      </Box>

      {isLoading && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Spinner />
        </Box>
      )}

      <Box
        gap="s48"
        paddingTop="s24"
        style={{ maxWidth: '1600px', width: '100%', marginLeft: 'auto', marginRight: 'auto' }}
        id="marketplace-modules-list"
      >
        {modulesByCategory.map(({ category, modules: categoryModules }) => (
          <Box key={category?.id || 'uncategorized'} gap="s24">
            {renderCategoryHeader(category)}
            <AutoGrid gap="4">{categoryModules.map(renderModuleCard)}</AutoGrid>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default List
